import { resolveTimeZone } from '@galapagos/shared';
import { DateTime } from 'luxon';

/**
 * Slot labels read in the language on screen. US regional formats in both
 * languages, since every member signing up is in the US.
 */
export function labelLocale(language: string): string {
  return language === 'es' ? 'es-US' : 'en-US';
}

/** One bookable time: the server's own instant string, and how it reads. */
export interface BookingTime {
  startsAt: string;
  label: string;
}

/** The bookable times on one calendar day in the member's zone. */
export interface BookingDay {
  /** ISO date in the member's zone, e.g. `2026-10-14`. */
  key: string;
  weekdayLabel: string;
  dayLabel: string;
  times: BookingTime[];
}

/** The pieces of the book button's label for a chosen time. */
export type BookLabelParts = {
  weekday: string;
  month: string;
  day: string;
  time: string;
};

/**
 * The zone name the browser reports, or `null` when it cannot say. Only the
 * name is read; every conversion happens in Luxon.
 */
export function readDeviceTimeZone(): string | null {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return zone && zone.length > 0 ? zone : null;
  } catch {
    return null;
  }
}

/**
 * The zone a signing-up member sees times in. Before an account exists the
 * device is the member's only zone source, so it goes through the shared
 * resolver, which supplies the repo default for a missing or unsupported name.
 */
export function memberTimeZone(deviceZone: string | null = readDeviceTimeZone()): string {
  return resolveTimeZone(deviceZone);
}

function inZone(instant: string, zone: string, language: string): DateTime {
  return DateTime.fromISO(instant, { zone }).setLocale(labelLocale(language));
}

/**
 * Groups slot instants by calendar day in `zone`, days and times ascending.
 * Each time keeps the server's string so booking sends back exactly what was
 * offered; an instant Luxon cannot read is left out rather than mislabelled.
 */
export function groupSlotsByDay(
  slots: readonly string[],
  zone: string,
  language = 'en',
): BookingDay[] {
  const parsed = slots
    .map((startsAt) => ({ startsAt, at: inZone(startsAt, zone, language) }))
    .filter(({ at }) => at.isValid)
    .sort((a, b) => a.at.toMillis() - b.at.toMillis());
  const days = new Map<string, BookingDay>();
  for (const { startsAt, at } of parsed) {
    const key = at.toISODate();
    if (key === null) continue;
    const existing = days.get(key);
    const time = { startsAt, label: at.toFormat('t') };
    if (existing) {
      existing.times.push(time);
    } else {
      days.set(key, {
        key,
        weekdayLabel: at.toFormat('ccc'),
        dayLabel: at.toFormat('d'),
        times: [time],
      });
    }
  }
  return [...days.values()];
}

/**
 * The zone's plain name ("Eastern Time"), or the IANA name. Not the daylight
 * or standard name, which flips with the clock change while the times shown
 * stay right. Browsers older than the generic name option throw on it, so
 * they keep the daylight or standard name.
 */
export function zoneLabel(zone: string, at: string, language = 'en'): string {
  const instant = inZone(at, zone, language);
  try {
    const name = instant
      .toLocaleParts({ timeZoneName: 'longGeneric' })
      .find((part) => part.type === 'timeZoneName')?.value;
    return name ?? zone;
  } catch {
    return instant.offsetNameLong ?? zone;
  }
}

export function bookLabelParts(startsAt: string, zone: string, language = 'en'): BookLabelParts {
  const at = inZone(startsAt, zone, language);
  return {
    weekday: at.toFormat('ccc'),
    month: at.toFormat('LLL'),
    day: at.toFormat('d'),
    time: at.toFormat('t'),
  };
}
